import JSZip from 'jszip'
import { getRemoteMediaBlob } from './api'
import { openingPrompts } from './data/templates'
import { getMedia } from './offline'
import { getVisitTemplate } from './model'
import type { VisitSnapshot } from './types'

function download(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  anchor.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const safeName = (value: string) => value.trim().replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase()
const escapeCsv = (value: unknown) => `"${String(value ?? '').replaceAll('"', '""')}"`

export function buildOpportunityWorkbenchHandoff(visit: VisitSnapshot) {
  return {
    schemaVersion: 'climatise.opportunity-workbench-handoff.v1',
    exportedAt: new Date().toISOString(),
    source: {
      type: 'physical_site_visit',
      visitId: visit.id,
      visitSlug: visit.slug,
      templateId: visit.templateId,
      templateVersion: visit.templateVersion,
    },
    developmentStatuses: ['candidate', 'needs_data', 'quantified', 'assessor_reviewed', 'approved'],
    visit,
  }
}

export function exportJson(visit: VisitSnapshot) {
  download(
    new Blob([JSON.stringify(visit, null, 2)], { type: 'application/json' }),
    `${safeName(visit.details.client)}-${safeName(visit.details.siteName)}-site-visit.json`,
  )
}

export function observationsCsv(visit: VisitSnapshot) {
  const template = getVisitTemplate(visit)
  const header = [
    'Observation ID', 'Category', 'Area', 'Asset', 'Condition', 'Observation',
    'Evidence source', 'Severity', 'Confidence', 'Constraint', 'Follow-up', 'Owner', 'Date',
  ]
  const rows = visit.observations.map((item) => [
    item.id,
    template.categories.find((category) => category.id === item.categoryId)?.label ?? item.categoryId,
    visit.areas.find((area) => area.id === item.areaId)?.name ?? '',
    item.assetLabel,
    item.condition,
    item.note,
    item.evidenceSource.join('; '),
    item.severity ?? '',
    item.confidence ?? '',
    item.implementationConstraint,
    item.followUp,
    item.followUpOwner,
    item.followUpDate,
  ])
  return [header, ...rows].map((row) => row.map(escapeCsv).join(',')).join('\n')
}

export function exportCsv(visit: VisitSnapshot) {
  download(
    new Blob([observationsCsv(visit)], { type: 'text/csv;charset=utf-8' }),
    `${safeName(visit.details.client)}-${safeName(visit.details.siteName)}-observations.csv`,
  )
}

export async function exportEvidenceZip(visit: VisitSnapshot) {
  const zip = new JSZip()
  zip.file('visit.json', JSON.stringify(visit, null, 2))
  zip.file('opportunity-workbench-handoff.json', JSON.stringify(buildOpportunityWorkbenchHandoff(visit), null, 2))
  zip.file('observations.csv', observationsCsv(visit))
  const mediaFolder = zip.folder('evidence')
  const missing: string[] = []
  for (const evidence of visit.evidence.filter((item) => !item.deletedAt)) {
    const media = await getMedia(evidence.id)
    if (media) {
      mediaFolder?.file(`${evidence.id}-${evidence.fileName}`, media.blob)
    } else if (!evidence.localOnly) {
      const blob = await getRemoteMediaBlob(evidence.id).catch(() => null)
      if (blob) mediaFolder?.file(`${evidence.id}-${evidence.fileName}`, blob)
      else missing.push(evidence.fileName)
    } else {
      missing.push(evidence.fileName)
    }
  }
  if (missing.length) throw new Error(`Evidence export stopped because ${missing.length} file(s) could not be retrieved: ${missing.join(', ')}`)
  download(
    await zip.generateAsync({ type: 'blob' }),
    `${safeName(visit.details.client)}-${safeName(visit.details.siteName)}-workbench-package.zip`,
  )
}

function htmlEscape(value: unknown) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
}

async function imageDataUrl(evidenceId: string, localOnly: boolean) {
  const local = await getMedia(evidenceId)
  let blob = local?.blob
  if (!blob && !localOnly) {
    blob = await getRemoteMediaBlob(evidenceId)
  }
  if (!blob) return null
  return new Promise<string>((resolve) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.readAsDataURL(blob)
  })
}

export async function printVisit(visit: VisitSnapshot) {
  const popup = window.open('', '_blank')
  if (!popup) {
    window.alert('Allow pop-ups to open the printable visit report.')
    return
  }
  popup.opener = null
  popup.document.write('<p style="font:16px sans-serif;padding:2rem">Preparing visit evidence…</p>')
  const template = getVisitTemplate(visit)
  const printableMedia = new Map<string, string>()
  const missingPrintable: string[] = []
  for (const evidence of visit.evidence.filter((item) => (
    !item.deletedAt && (item.kind === 'photo' || item.kind === 'sketch')
  ))) {
    const url = await imageDataUrl(evidence.id, evidence.localOnly).catch(() => null)
    if (url) printableMedia.set(evidence.id, url)
    else missingPrintable.push(evidence.fileName)
  }
  const observations = visit.observations.map((item) => `
    <article>
      <h3>${htmlEscape(item.assetLabel || 'Observation')}</h3>
      <p class="meta">${htmlEscape(template.categories.find((category) => category.id === item.categoryId)?.label)} · ${htmlEscape(visit.areas.find((area) => area.id === item.areaId)?.name || 'No area')}</p>
      <p>${htmlEscape(item.note)}</p>
      ${item.dataPoints.length ? `<ul>${item.dataPoints.map((point) => `<li>${htmlEscape(point.label)}: ${htmlEscape(point.value)} ${htmlEscape(point.unit)} (${htmlEscape(point.quality)})</li>`).join('')}</ul>` : ''}
      ${item.evidenceIds.map((id) => {
        const evidence = visit.evidence.find((entry) => entry.id === id)
        const image = printableMedia.get(id)
        if (!evidence || evidence.deletedAt) return ''
        if (image) return `<figure><img src="${image}" alt="${htmlEscape(evidence.caption || evidence.fileName)}"><figcaption>${htmlEscape(evidence.caption || evidence.fileName)}</figcaption></figure>`
        if (evidence.kind === 'audio') return `<p><strong>Voice note:</strong> ${htmlEscape(evidence.transcript || evidence.caption || evidence.fileName)}</p>`
        return `<p><strong>Evidence:</strong> ${htmlEscape(evidence.caption || evidence.fileName)}</p>`
      }).join('')}
      ${item.followUp ? `<p><strong>Follow-up:</strong> ${htmlEscape(item.followUp)} · ${htmlEscape(item.followUpOwner || 'No owner')} ${htmlEscape(item.followUpDate)}</p>` : ''}
    </article>
  `).join('')
  const opportunities = visit.opportunities.map((item) => `
    <article>
      <h3>${htmlEscape(item.title || 'Untitled opportunity candidate')}</h3>
      <p>${htmlEscape(item.observed)}</p>
      <p><strong>Suggested action:</strong> ${htmlEscape(item.recommendation)}</p>
      <p><strong>Areas:</strong> ${htmlEscape(item.areaIds.map((id) => visit.areas.find((area) => area.id === id)?.name).filter(Boolean).join(', ') || 'Not set')}</p>
      <p><strong>Likely benefits:</strong> ${htmlEscape(item.benefitTypes.join(', ') || 'Not set')}</p>
      <p><strong>Supporting observations:</strong> ${htmlEscape(item.observationIds.map((id) => visit.observations.find((observation) => observation.id === id)?.assetLabel || id).join(', ') || 'None linked')}</p>
      ${item.dataNeeded ? `<p><strong>Data still needed:</strong> ${htmlEscape(item.dataNeeded)}</p>` : ''}
      <p><strong>Implementation difficulty:</strong> ${htmlEscape(item.difficulty)}</p>
      ${item.constraints ? `<p><strong>Constraints:</strong> ${htmlEscape(item.constraints)}</p>` : ''}
      ${item.responsibleContact ? `<p><strong>Responsible contact:</strong> ${htmlEscape(item.responsibleContact)}</p>` : ''}
      ${item.targetFollowUp ? `<p><strong>Target follow-up:</strong> ${htmlEscape(item.targetFollowUp)}</p>` : ''}
      ${item.disposition === 'considered_not_pursued' ? `<p><strong>Reason not pursued:</strong> ${htmlEscape(item.notPursuedReason)}</p>` : ''}
      <p class="meta">Priority ${htmlEscape(item.priority)} · Confidence ${htmlEscape(item.confidence)} · ${htmlEscape(item.disposition.replaceAll('_', ' '))}</p>
    </article>
  `).join('')
  const categoryReview = template.categories.map((category) => {
    const progress = visit.categoryProgress[category.id]
    return `<tr><td>${htmlEscape(category.label)}</td><td>${htmlEscape(progress?.state?.replaceAll('_', ' ') || 'not reviewed')}</td><td>${htmlEscape(progress?.note || '')}</td></tr>`
  }).join('')
  const quickChecks = template.quickChecks.map((item) => {
    const response = visit.quickChecks[item.id]
    return `<tr><td>${htmlEscape(item.label)}</td><td>${htmlEscape(response?.answer?.toUpperCase() || 'Not answered')}</td><td>${htmlEscape(response?.evidence || '')}</td></tr>`
  }).join('')
  const areas = visit.areas.map((area) => `<li><strong>${htmlEscape(area.name)}</strong> — ${area.accessible ? 'Accessed' : `Not accessed: ${htmlEscape(area.excludedReason || 'reason not recorded')}`}</li>`).join('')
  const closeOut = Object.entries(visit.closing)
    .filter(([, value]) => value)
    .map(([key, value]) => `<p><strong>${htmlEscape(key.replace(/([A-Z])/g, ' $1'))}:</strong> ${htmlEscape(value)}</p>`)
    .join('')
  const opening = Object.entries(visit.openingResponses)
    .filter(([, value]) => value)
    .map(([key, value]) => {
      const index = Number(key.split('-')[1]) - 1
      return `<p><strong>${htmlEscape(openingPrompts[index] || key)}:</strong> ${htmlEscape(value)}</p>`
    }).join('')
  popup.document.open()
  popup.document.write(`<!doctype html><html><head><title>Site visit — ${htmlEscape(visit.details.siteName)}</title>
    <style>
      body{font:14px Inter,Arial,sans-serif;color:#172033;max-width:900px;margin:40px auto;line-height:1.5}
      h1,h2,h3{font-family:Arial,sans-serif;color:#1d3557}h1{font-size:30px}h2{border-bottom:2px solid #3076e2;padding-bottom:6px;margin-top:32px}
      article{break-inside:avoid;border:1px solid #dce2ea;border-radius:8px;padding:12px 16px;margin:10px 0}.meta{color:#5e6b7e}
      figure{margin:12px 0}figure img{display:block;max-width:100%;max-height:420px;border:1px solid #dce2ea;border-radius:6px}figcaption{margin-top:4px;color:#5e6b7e;font-size:11px}
      dl{display:grid;grid-template-columns:180px 1fr;gap:6px 16px}dt{color:#5e6b7e}dd{margin:0}
      table{width:100%;border-collapse:collapse}th,td{padding:7px;border:1px solid #dce2ea;text-align:left;vertical-align:top}th{background:#f5f7fb}
      @media print{body{margin:0}button{display:none}}
    </style></head><body>
    <h1>Physical site visit record</h1>
    <dl>
      <dt>Client</dt><dd>${htmlEscape(visit.details.client)}</dd>
      <dt>Site</dt><dd>${htmlEscape(visit.details.siteName)}</dd>
      <dt>Address</dt><dd>${htmlEscape(visit.details.address)}</dd>
      <dt>Visit date</dt><dd>${htmlEscape(visit.details.visitDate)}</dd>
      <dt>Auditor(s)</dt><dd>${htmlEscape(visit.details.auditors)}</dd>
      <dt>Template</dt><dd>${htmlEscape(template.label)} v${template.version}</dd>
      <dt>Actual site activity</dt><dd>${htmlEscape(visit.details.actualActivity)}</dd>
      <dt>Visit conditions</dt><dd>${htmlEscape(visit.details.visitConditions)}</dd>
    </dl>
    <h2>Areas and access</h2><ul>${areas || '<li>No areas recorded.</li>'}</ul>
    <h2>Opening discussion</h2>${opening || '<p>No opening responses recorded.</p>'}
    <h2>Category review</h2><table><thead><tr><th>Category</th><th>State</th><th>Reason / note</th></tr></thead><tbody>${categoryReview}</tbody></table>
    <h2>Operational quick checks</h2><table><thead><tr><th>Check</th><th>Answer</th><th>Evidence</th></tr></thead><tbody>${quickChecks}</tbody></table>
    <h2>Observations</h2>${observations || '<p>No observations recorded.</p>'}
    ${missingPrintable.length ? `<p><strong>Evidence warning:</strong> The following image files could not be embedded and should be retrieved separately: ${htmlEscape(missingPrintable.join(', '))}</p>` : ''}
    <h2>Opportunity candidates</h2>${opportunities || '<p>No opportunity candidates were flagged on site.</p>'}
    <h2>Close-out</h2>${closeOut || '<p>No close-out recorded.</p>'}
    <script>window.onload=()=>window.print()</script></body></html>`)
  popup.document.close()
}
